import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import EnchargeForm from './components/EnchargeForm';
import './App.css';

const {FiArrowRight, FiTrendingUp, FiUsers, FiTarget, FiX} = FiIcons;

function App() {
  const [modalImage, setModalImage] = useState(null);
  const [isVerticalImage, setIsVerticalImage] = useState(false);

  const fadeInUp = {
    initial: {opacity: 0, y: 60},
    animate: {opacity: 1, y: 0},
    transition: {duration: 0.6}
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const openModal = (imageSrc, vertical = false) => {
    setModalImage(imageSrc);
    setIsVerticalImage(vertical);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalImage(null);
    setIsVerticalImage(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Image Modal */}
      <AnimatePresence>
        {modalImage && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
            onClick={closeModal}
          >
            <motion.div
              initial={{scale: 0.9, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.9, opacity: 0}}
              className={`relative ${isVerticalImage ? 'max-w-md' : 'max-w-7xl'} max-h-[90vh] w-full`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 text-white hover:text-[#5EDDBF] transition-colors p-2"
              >
                <SafeIcon icon={FiX} className="text-3xl" />
              </button>
              <img
                src={modalImage}
                alt="Expanded view"
                className="w-full h-full object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Subtle accent background for hero */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#5EDDBF]/5 via-white to-white"></div>

        <div className="container mx-auto px-6 py-16 lg:py-24 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerChildren}
          >
            <motion.img
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764166562633-YouTube%20Thumbnail%201280x720.png"
              alt="First 500 Followers System"
              className="w-full max-w-xs mx-auto rounded-2xl shadow-2xl mb-12 border border-slate-200"
              variants={fadeInUp}
            />

            <motion.h1
              className="text-4xl lg:text-6xl xl:text-7xl font-bold text-slate-900 mb-8 leading-tight"
              variants={fadeInUp}
            >
              The System to Get Your{' '}
              <span className="text-slate-900 relative inline-block">
                <span className="relative z-10 bg-[#5EDDBF]/30 px-2 rounded">First 500 Followers</span>
              </span>
            </motion.h1>

            <motion.p
              className="text-xl lg:text-2xl text-slate-600 mb-12 leading-relaxed max-w-3xl mx-auto font-medium"
              variants={fadeInUp}
            >
              <span className="font-bold text-slate-900">Non-fiction Writers:</span> Learn how to get your{' '}
              <span className="bg-[#5EDDBF]/30 font-semibold px-1 rounded">First 500 Followers</span> through a proven, repeatable system—not guesswork—in the free 21-day email training
            </motion.p>

            {/* Encharge Opt-In Form */}
            <motion.div className="max-w-2xl mx-auto optin" variants={fadeInUp}>
              <EnchargeForm />
            </motion.div>

            <motion.p className="text-slate-500 mt-6 italic" variants={fadeInUp}>
              Join 1,652+ creators who are done guessing and have started building a real business.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Problems Section */}
      <motion.div
        className="py-20 bg-slate-50"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-12 text-center"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              Sound Familiar?
            </motion.h2>

            <motion.div
              className="space-y-6"
              initial="initial"
              whileInView="animate"
              variants={staggerChildren}
              viewport={{once: true}}
            >
              {[
                {text: "You hit \"publish\" on a piece of content you poured your soul into... ", bold: "and get nothing but silence."},
                {text: "You see creators with half your expertise building huge audiences and wonder ", italic: "what the hell am I doing wrong?"},
                {text: "You have dozens of drafts and brilliant ideas, but you're so stuck on making them \"perfect\" that ", bold: "you never publish."},
                {text: "You know your knowledge could change someone's life... If ", highlight: "you could just stop the scroll."}
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  variants={fadeInUp}
                >
                  <div className="w-2 h-2 bg-[#5EDDBF] rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-slate-700 text-lg leading-relaxed">
                    {item.text}
                    {item.bold && <span className="font-bold">{item.bold}</span>}
                    {item.italic && <span className="italic">{item.italic}</span>}
                    {item.highlight && <span className="bg-[#5EDDBF]/30 font-semibold px-1 rounded">{item.highlight}</span>}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="mt-12 p-8 bg-[#5EDDBF]/10 rounded-xl border border-[#5EDDBF]/30"
              initial={{opacity: 0, scale: 0.95}}
              whileInView={{opacity: 1, scale: 1}}
              transition={{duration: 0.6, delay: 0.3}}
              viewport={{once: true}}
            >
              <p className="text-xl text-slate-900 font-bold mb-4">The problem isn't your knowledge. It's your process.</p>
              <p className="text-slate-700 text-lg leading-relaxed">
                You're treating content creation like a lottery ticket when it should be treated like an engineering project. You don't need more ideas. You need a system.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Student Results Section */}
      <motion.div
        className="py-20 bg-white border-t border-slate-100"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 text-center"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              Real Results from Real Students
            </motion.h2>

            <motion.p
              className="text-xl text-slate-600 mb-12 text-center max-w-3xl mx-auto"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              viewport={{once: true}}
            >
              The system works because it's based on human psychology, not algorithm hacks. Here's what happens when you apply it.
            </motion.p>

            {/* Case Study 1: Ngangjoh */}
            <motion.div
              className="space-y-6 mb-8"
              initial="initial"
              whileInView="animate"
              variants={staggerChildren}
              viewport={{once: true}}
            >
              {/* Horizontal Image - Email Screenshot */}
              <motion.div variants={fadeInUp}>
                <div className="mb-4">
                  <h3 className="text-slate-900 text-xl md:text-2xl font-bold mb-2">Ngangjoh</h3>
                  <p className="text-slate-700 text-base md:text-lg font-medium">
                    From 3k views/week to <span className="text-[#5EDDBF] font-bold">47k views</span> after following the process.
                  </p>
                </div>
                <div
                  className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 cursor-pointer"
                  onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764183301438-Ngangjoh%20-%2047k%20views.png")}
                >
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764183301438-Ngangjoh%20-%2047k%20views.png"
                    alt="Ngangjoh Success Story"
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
              </motion.div>

              {/* Vertical Images - Stats Screenshots Side by Side */}
              <motion.div variants={fadeInUp} className="flex flex-col md:flex-row justify-center gap-8 md:gap-16">
                {/* Threads Stats (Left) */}
                <div className="flex flex-col items-center">
                  <div className="mb-4 max-w-[200px] mx-auto">
                    <p className="text-slate-900 font-bold text-lg text-center">
                      <span className="text-[#5EDDBF]">Explosive</span> Growth on Threads...
                    </p>
                  </div>
                  <div
                    className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 w-[200px] h-[400px] mx-auto cursor-pointer"
                    onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764183304995-Ngangjoh%20-%20stats.jpg", true)}
                  >
                    <img
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764183304995-Ngangjoh%20-%20stats.jpg"
                      alt="Ngangjoh Stats"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
                </div>

                {/* LinkedIn Stats (Right) */}
                <div className="flex flex-col items-center">
                  <div className="mb-4 max-w-[200px] mx-auto">
                    <p className="text-slate-900 font-bold text-lg text-center">
                      ...And LinkedIn
                    </p>
                  </div>
                  <div
                    className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 w-[200px] h-[400px] mx-auto cursor-pointer"
                    onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764184959270-Screenshot%202025-11-26%20at%2012.22.19%C3%A2%C2%80%C2%AFPM.png", true)}
                  >
                    <img
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764184959270-Screenshot%202025-11-26%20at%2012.22.19%C3%A2%C2%80%C2%AFPM.png"
                      alt="Martin LinkedIn Post"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
                </div>
              </motion.div>

              {/* Martin 46k Impressions */}
              <motion.div variants={fadeInUp} className="mt-12 border-t border-slate-100 pt-12">
                <div className="mb-6 text-center max-w-2xl mx-auto">
                  <h3 className="text-slate-900 text-xl md:text-2xl font-bold mb-2">Martin's Viral Success</h3>
                  <p className="text-slate-700 text-base md:text-lg font-medium">
                    A single LinkedIn post generating <span className="text-[#5EDDBF] font-bold">46k+ impressions</span>, 4,445 likes, 163 comments, and 29 reposts.
                  </p>
                </div>
                <div
                  className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 cursor-pointer max-w-3xl mx-auto"
                  onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764185581720-Martin%20Linked%20In%20Celebration%2046k%20impressions.png")}
                >
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764185581720-Martin%20Linked%20In%20Celebration%2046k%20impressions.png"
                    alt="Martin LinkedIn Stats 46k Impressions"
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
              </motion.div>

              {/* Martin Medium Article 1.1k Claps */}
              <motion.div variants={fadeInUp} className="mt-12 border-t border-slate-100 pt-12">
                <div className="mb-6 text-center max-w-2xl mx-auto">
                  <h3 className="text-slate-900 text-xl md:text-2xl font-bold mb-2">Building Momentum on Medium</h3>
                  <p className="text-slate-700 text-base md:text-lg font-medium">
                    One of Martin's articles hit <span className="text-[#5EDDBF] font-bold">1.1K claps</span> and 19 comments, and it's just picking up more and more steam.
                  </p>
                </div>
                <div
                  className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 cursor-pointer max-w-3xl mx-auto"
                  onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764185807258-Martin%201.1k%20views.png")}
                >
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764185807258-Martin%201.1k%20views.png"
                    alt="Martin Medium Article 1.1k Claps"
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
              </motion.div>

              {/* Barry's Quora Success */}
              <motion.div variants={fadeInUp} className="mt-12 border-t border-slate-100 pt-12">
                <div className="mb-6 text-center max-w-3xl mx-auto">
                  <h3 className="text-slate-900 text-xl md:text-2xl font-bold mb-4">Barry's First 10k Views on Quora</h3>
                  <p className="text-slate-700 text-base md:text-lg font-medium italic mb-2 relative">
                    <span className="text-4xl text-[#5EDDBF] absolute -top-4 -left-4 opacity-50">"</span>
                    I was quite astounded to say the least. It is my most significant achievement as a writer so far.
                    <br className="my-2" />
                    <span className="text-[#5EDDBF] font-bold not-italic bg-[#5EDDBF]/10 px-1 rounded">I now know there is an audience who reads what I write.</span>
                    <span className="text-4xl text-[#5EDDBF] absolute -bottom-8 -right-4 opacity-50">"</span>
                  </p>
                </div>
                <div
                  className="relative group overflow-hidden rounded-2xl shadow-lg border border-slate-200 bg-slate-100 cursor-pointer max-w-3xl mx-auto mt-8"
                  onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764186678815-Barry%2010k.png")}
                >
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764186678815-Barry%2010k.png"
                    alt="Barry Quora 10k Views Stats"
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="text-slate-500 text-sm mt-2 text-center italic">Click to expand</p>
              </motion.div>
            </motion.div>

            {/* Placeholders for Future Case Studies */}
          </div>
        </div>
      </motion.div>

      {/* Solution Section */}
      <motion.div
        className="py-20 bg-slate-50"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 text-center"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              The <span className="bg-[#5EDDBF]/30 px-2 rounded">First 500 Followers</span> system is the Solution. I'm Giving It to You for{' '}
              <span className="text-[#5EDDBF] font-extrabold">Free.</span>
            </motion.h2>

            <motion.p
              className="text-lg text-slate-600 mb-12 text-center leading-relaxed"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              viewport={{once: true}}
            >
              It's a 21-day email series outlining the systematic approach to building an audience that{' '}
              <span className="bg-[#5EDDBF]/30 font-semibold px-1 rounded">actually gives a sh*t about what you have to say.</span>
            </motion.p>

            <motion.h3
              className="text-2xl font-bold text-slate-900 mb-8 text-center"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              viewport={{once: true}}
            >
              Here's what you'll get:
            </motion.h3>

            <motion.div
              className="space-y-8 mb-12"
              initial="initial"
              whileInView="animate"
              variants={staggerChildren}
              viewport={{once: true}}
            >
              {[
                {title: "Act I: The Habit", description: "Learn how to break the cycle of perfectionism and build an unbreakable habit of consistent publishing. You'll go from overthinking to executing.", icon: FiTarget},
                {title: "Act II: The System", description: "Get the \"Viral Validated Method\"—a step-by-step process to find proven ideas and \"train\" the algorithm to find your ideal readers. No more shooting in the dark.", icon: FiTrendingUp},
                {title: "Act III: The Art", description: "Master the art of packaging your ideas to stop the scroll, command attention, and become un-ignorable in a crowded space.", icon: FiUsers}
              ].map((act, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-6 p-8 bg-white rounded-xl border border-slate-200 hover:border-[#5EDDBF] transition-all duration-300 shadow-sm hover:shadow-md"
                  variants={fadeInUp}
                >
                  <div className="bg-[#5EDDBF] p-3 rounded-lg flex-shrink-0 shadow-sm">
                    <SafeIcon icon={act.icon} className="text-2xl text-slate-900" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-3">{act.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{act.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="text-center"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              <motion.button
                style={{color: 'rgb(253, 252, 252)'}}
                className="optin bg-[#5EDDBF] text-slate-900 px-8 py-4 rounded-lg text-xl font-bold hover:bg-[#4CCCA8] transition-all duration-300 shadow-lg hover:shadow-[#5EDDBF]/30 transform hover:-translate-y-1 inline-flex items-center gap-3"
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={() => document.getElementById('signup-form').scrollIntoView({behavior: 'smooth'})}
              >
                SEND ME THE FREE TRAINING
                <SafeIcon icon={FiArrowRight} className="text-xl" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Interactive Training Section */}
      <motion.div
        className="py-20 bg-white"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              This is <span className="text-[#5EDDBF] font-extrabold animate-pulse-slow">NOT</span> a Course.
            </motion.h2>

            <motion.p
              className="text-xl text-slate-600 mb-8 leading-relaxed"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              viewport={{once: true}}
            >
              This is an <strong className="text-slate-900 bg-[#5EDDBF]/20 px-1 rounded">interactive training</strong>. When you reply to any email, it goes straight to my personal inbox—and I reply to every single one.
            </motion.p>

            <motion.div
              className="grid md:grid-cols-3 gap-6 mb-12"
              initial="initial"
              whileInView="animate"
              variants={staggerChildren}
              viewport={{once: true}}
            >
              {[
                "Got a question about your content strategy? Hit reply.",
                "Stuck on finding your audience? Hit reply.",
                "Want me to review your first article? Hit reply."
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="p-6 bg-slate-50 rounded-lg border border-slate-200 shadow-sm"
                  variants={fadeInUp}
                >
                  <p className="text-slate-700 font-medium">{item}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.p
              className="text-lg text-slate-600 mb-6 leading-relaxed"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              viewport={{once: true}}
            >
              Most "gurus" hide behind support teams and automation. I'm building this business in public, which means you get direct access to me throughout your entire 21-day journey.
            </motion.p>

            <motion.p
              className="text-xl text-slate-900 font-bold"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              viewport={{once: true}}
            >
              You're not just getting information. You're getting a mentor.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Credibility Section - Modified */}
      <motion.div
        className="py-20 bg-slate-50"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Intro Section with Grid */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <motion.div
                initial={{opacity: 0, x: -30}}
                whileInView={{opacity: 1, x: 0}}
                transition={{duration: 0.6}}
                viewport={{once: true}}
              >
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 text-left">
                  Why Listen to Me?
                </h2>
                <p className="text-xl text-slate-600 leading-relaxed text-left">
                  I don't just teach this stuff. I live it. My entire business is built on the "build in public" philosophy—I'm in the trenches with you, testing and validating every strategy I share.
                </p>
              </motion.div>

              <motion.div
                className="flex flex-col items-center md:items-start"
                initial={{opacity: 0, x: 30}}
                whileInView={{opacity: 1, x: 0}}
                transition={{duration: 0.6, delay: 0.2}}
                viewport={{once: true}}
              >
                <div className="flex flex-col items-center w-full">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-[#5EDDBF] rounded-2xl transform rotate-3 scale-105 opacity-20"></div>
                    <img
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764187260142-blob"
                      alt="Gabriel Klingman"
                      className="relative w-64 h-64 object-cover rounded-2xl shadow-xl transform -rotate-2 hover:rotate-0 transition-transform duration-300 border-4 border-white"
                    />
                  </div>
                  <p className="text-xl font-bold text-slate-900">Hey! I'm Gabriel Klingman</p>
                </div>
              </motion.div>
            </div>

            {/* Stats Grid */}
            <div className="max-w-4xl mx-auto">
              <motion.div
                className="grid md:grid-cols-2 gap-8 mb-12"
                initial="initial"
                whileInView="animate"
                variants={staggerChildren}
                viewport={{once: true}}
              >
                <motion.div className="p-8 bg-white rounded-xl border border-slate-200" variants={fadeInUp}>
                  <div
                    className="relative group overflow-hidden rounded-lg shadow-md mb-6 border border-slate-200 cursor-pointer"
                    onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764110632171-35%20followers%20to%205k%20followers%20in%2012%20monthsPNG%20image.png")}
                  >
                    <img
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764110632171-35%20followers%20to%205k%20followers%20in%2012%20monthsPNG%20image.png"
                      alt="Social proof stats showing growth"
                      className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-slate-500 text-sm mb-4 text-center italic">Click to expand</p>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">From 35 to 5,000+ Followers in 12 Months</h3>
                  <div className="bg-slate-50 rounded-lg p-6 border border-slate-100 shadow-sm">
                    <div style={{color: 'rgb(94, 221, 191)'}} className="text-[#5EDDBF] text-4xl font-bold mb-2">5,882%</div>
                    <div className="text-slate-500 font-medium">Growth Rate</div>
                  </div>
                </motion.div>

                <motion.div className="p-8 bg-white rounded-xl border border-slate-200" variants={fadeInUp}>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div
                      className="relative group overflow-hidden rounded-lg shadow-sm border border-slate-200 cursor-pointer"
                      onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764111013174-Screenshot%202024-07-03%20at%2010.37.46%C3%A2%C2%80%C2%AFAM.png")}
                    >
                      <img
                        src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764111013174-Screenshot%202024-07-03%20at%2010.37.46%C3%A2%C2%80%C2%AFAM.png"
                        alt="Stats proof 1"
                        className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div
                      className="relative group overflow-hidden rounded-lg shadow-sm border border-slate-200 cursor-pointer"
                      onClick={() => openModal("https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764111021851-Screenshot%202024-07-01%20at%208.12.38%C3%A2%C2%80%C2%AFAM.png")}
                    >
                      <img
                        src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1764111021851-Screenshot%202024-07-01%20at%208.12.38%C3%A2%C2%80%C2%AFAM.png"
                        alt="Stats proof 2"
                        className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </div>
                  <p className="text-slate-500 text-sm mb-4 text-center italic">Click images to expand</p>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">From 22 views per month to over 15,000 views per month</h3>
                  <div className="bg-slate-50 rounded-lg p-6 border border-slate-100 shadow-sm">
                    <div className="text-[#5EDDBF] text-4xl font-bold mb-2">68,182%</div>
                    <div className="text-slate-500 font-medium">Increase in Reach</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.p
                className="text-lg text-slate-600 mb-8 text-center leading-relaxed"
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: 0.3}}
                viewport={{once: true}}
              >
                This isn't about vanity metrics. It's about proving that when you focus on a system that serves a specific person, the algorithm has no choice but to find them for you.
              </motion.p>

              <motion.blockquote
                className="bg-white p-8 rounded-xl border-l-4 border-[#5EDDBF] italic text-slate-600 text-lg leading-relaxed shadow-sm"
                initial={{opacity: 0, x: -30}}
                whileInView={{opacity: 1, x: 0}}
                transition={{duration: 0.6}}
                viewport={{once: true}}
              >
                "I was quite astounded to say the least. It is my most significant achievement as a writer so far. I now know there is an audience who reads what I write."
                <footer className="text-slate-900 mt-4 not-italic font-bold flex items-center gap-2">
                  <span className="w-8 h-px bg-slate-300"></span>
                  Barry, getting his first 10k views on Quora
                </footer>
              </motion.blockquote>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Final CTA Section */}
      <motion.div
        className="py-20 bg-gradient-to-b from-white to-[#5EDDBF]/10"
        initial={{opacity: 0}}
        whileInView={{opacity: 1}}
        transition={{duration: 0.8}}
        viewport={{once: true}}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8 leading-tight"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              viewport={{once: true}}
            >
              Your Knowledge Has Value. Let's Build the Audience That Needs It.
            </motion.h2>

            <motion.p
              className="text-xl text-slate-600 mb-8 leading-relaxed"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              viewport={{once: true}}
            >
              Stop guessing. Stop wasting your wisdom on content that disappears into the void.
            </motion.p>

            <motion.p
              className="text-lg text-slate-600 mb-12 leading-relaxed"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              viewport={{once: true}}
            >
              Get the complete 21-day system that will teach you how to build your foundation and attract your{' '}
              <span className="bg-[#5EDDBF]/30 font-semibold px-1 rounded">First 500 Followers</span>—the ones who will become your customers, advocates, and community.
            </motion.p>

            {/* Signup Form */}
            <motion.div
              id="signup-form"
              className="max-w-2xl mx-auto optin"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              viewport={{once: true}}
            >
              <EnchargeForm />

              <motion.p
                className="text-slate-400 mt-4 text-sm"
                initial={{opacity: 0}}
                whileInView={{opacity: 1}}
                transition={{duration: 0.6, delay: 0.5}}
                viewport={{once: true}}
              >
                This is a free training. No credit card. No one-time-offer. No BS. Unsubscribe at any time.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default App;